/**
 * RequireElevationAccess
 *
 * Layout-route guard for everything behind the Elevation gate. Three levels
 * (Wes, 2026-10-18; see lib/elevationAccess.ts):
 *
 *   - Owner pages (Elevation's own knowledge system, ELEVATION_OWNER_ONLY_PATHS)
 *     open only for the owner.
 *   - Every other page behind the gate, including the team hub at /home,
 *     opens for the team (the owner included).
 *   - Customers open nothing here.
 *
 * Anyone below a page's level is sent to /prioritization/dashboard. While it
 * is not yet known who is signed in, nothing renders, so a page is never shown
 * to someone it is not for.
 *
 * For everyone who is let in, both switches still apply:
 *   1. The `elevation_platform` workspace module is enabled for this workspace.
 *   2. The `elevation_nav` individual feature flag is enabled for this user.
 *
 * While the module state is still loading (empty object from useWorkspaceModules),
 * we render the outlet optimistically to avoid a flash of redirect. That happens
 * only after the level check above, so it never lets anyone in early.
 *
 * Light-mode enforcement for Elevation is handled by ElevationThemeEnforcer
 * in App.tsx via ThemeContext.setForcedLight — no DOM manipulation needed here.
 */

import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useFeatureFlag, FEATURE_FLAGS } from '../../lib/featureFlags';
import { isModuleEnabled, useWorkspaceModules } from '../../lib/workspaceModulesService';
import { elevationLevelForPath, hasElevationLevel, useElevationAccess } from '../../lib/elevationAccess';

const BLOCKED_REDIRECT = '/prioritization/dashboard';

export function RequireElevationAccess() {
  const { pathname } = useLocation();
  const access = useElevationAccess();
  const workspaceFeatures = useWorkspaceModules();
  const flagEnabled = useFeatureFlag(FEATURE_FLAGS.ELEVATION_NAV);

  if (access === 'pending') return null;
  if (!hasElevationLevel(access, elevationLevelForPath(pathname))) {
    return <Navigate to={BLOCKED_REDIRECT} replace />;
  }

  // While the workspace modules are still being fetched (empty object), allow
  // through to avoid a redirect flash on initial page load.
  const modulesLoading = Object.keys(workspaceFeatures).length === 0;
  if (modulesLoading) {
    return <Outlet />;
  }

  const moduleEnabled = isModuleEnabled(workspaceFeatures, 'elevation_platform');

  if (!moduleEnabled || !flagEnabled) {
    return <Navigate to={BLOCKED_REDIRECT} replace />;
  }

  return <Outlet />;
}
